// src/components/modules/CompetitiveHistoricalVisibility.tsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../../lib/supabase";
import RunComparisonBar from "../RunComparisonBar";
import Tooltip from "../Tooltip";
import {
  calculateDelta,
  getLatestRun,
  getPreviousRun,
  type RunHistoryItem,
} from "../../lib/runHistory";
import { getHistoryCompatibility } from "../../lib/historyCompatibility";
import { aggregateBrandMentionsByGovernance } from "../../lib/entityGovernanceRollup";
import { loadEntityGovernanceReviewTargets } from "../../lib/entityGovernanceApi";
import { createEntityRole, updateEntityRole } from "../../lib/competitiveSetupApi";
import type { TrustedEntityGovernanceRow } from "../../lib/entityGovernanceRollup";
import type { ScoreTreatment } from "../../types/competitiveSetup";

type CompetitiveHistoricalVisibilityProps = {
  account: string;
  customerBrand?: string | null;
  canReviewEntityGovernance?: boolean;
};

type PromptResultRow = {
  run_id: string;
  category: string | null;
  prompt_score: number | null;
  visibility_signal: string | null;
  brand_mentions: unknown;
  prompt_type?: string | null;
};

type CategorySummary = {
  category: string;
  prompts: number;
  avgScore: number;
  visibilityPct: number;
  mentionRate: number;
  strongSignalRate: number;
};

type MentionSummary = {
  entity: string;
  canonicalKey: string;
  mentions: number;
  rawNames: string[];
};

type ReviewTargetOption = {
  key: string;
  displayName: string;
  role: string;
  scoreTreatment: string | null;
  source: string | null;
  isCustomer: boolean;
};

type ScoreStatus = "weak" | "developing" | "strong";

const MAX_VISIBILITY_SCORE = 5;

function toNumber(value: unknown): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function clamp(value: number, min = 0, max = 100): number {
  return Math.max(min, Math.min(max, value));
}

function scoreToPercent(score: number): number {
  return clamp((score / MAX_VISIBILITY_SCORE) * 100);
}

function formatPercent(value: number): string {
  return `${value.toFixed(0)}%`;
}

export function calculateMentionCoverage(mentionCount: number, promptCount: number): number {
  if (promptCount <= 0) return 0;
  return Math.round((mentionCount / promptCount) * 100);
}

function isOrganicMarketMentionPrompt(row: PromptResultRow): boolean {
  const promptType = String(row.prompt_type || "").trim().toLowerCase();

  // Legacy runs may predate prompt_type. Preserve their historical behavior
  // by treating missing prompt types as organic rather than dropping them.
  return !promptType || promptType === "organic";
}

function parseBrandMentions(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value
      .filter((item): item is string => typeof item === "string")
      .map((item) => item.trim())
      .filter(Boolean);
  }

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return parsed
          .filter((item): item is string => typeof item === "string")
          .map((item) => item.trim())
          .filter(Boolean);
      }
    } catch {
      return value
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    }
  }

  return [];
}

function normalizeEntityName(value: string): string {
  return value.trim().toLowerCase();
}

function normalizeGovernanceRole(role: string | null | undefined): string {
  return String(role || "").trim().toLowerCase();
}

function getRoleLabel(role: string | null | undefined): string {
  const normalizedRole = normalizeGovernanceRole(role);

  if (normalizedRole === "direct_competitor") return "Direct competitor";
  if (normalizedRole === "adjacent_competitor") return "Adjacent competitor";
  if (normalizedRole === "suppress") return "Suppressed";
  if (
    normalizedRole === "customer" ||
    normalizedRole === "self" ||
    normalizedRole === "target_brand"
  ) {
    return "Your brand";
  }

  return normalizedRole ? normalizedRole.replace(/_/g, " ") : "Unassigned";
}

function isSelectableReviewTargetRole(role: string | null | undefined): boolean {
  const normalizedRole = normalizeGovernanceRole(role);
  return [
    "customer",
    "self",
    "target_brand",
    "direct_competitor",
    "adjacent_competitor",
    "suppress"
  ].includes(normalizedRole);
}

export function normalizeEntityReviewTarget(
  row: TrustedEntityGovernanceRow,
  customerBrand?: string | null
): ReviewTargetOption | null {
  const role = normalizeGovernanceRole(row.entity_role);

  if (!isSelectableReviewTargetRole(role)) return null;

  const displayName =
    row.normalized_entity_name?.trim() || row.raw_entity_name?.trim() || "";
  const key = normalizeEntityName(displayName);

  if (!key) return null;

  return {
    key,
    displayName,
    role,
    scoreTreatment: getScoreTreatmentForEntityRole(role),
    source: row.source || null,
    isCustomer:
      ["customer", "self", "target_brand"].includes(role) &&
      key === normalizeEntityName(customerBrand || "")
  };
}

export function getScoreTreatmentForEntityRole(
  role: string | null | undefined
): ScoreTreatment {
  const normalizedRole = normalizeGovernanceRole(role);

  if (["customer", "self", "target_brand"].includes(normalizedRole)) {
    return "include_primary";
  }

  if (["direct_competitor", "adjacent_competitor"].includes(normalizedRole)) {
    return "include_secondary";
  }

  if (normalizedRole === "suppress") {
    return "suppress_everywhere";
  }

  return "show_only";
}

function getMapExistingWriteConfig(role: string | null | undefined) {
  const normalizedRole = normalizeGovernanceRole(role);

  if (
    normalizedRole === "customer" ||
    normalizedRole === "self" ||
    normalizedRole === "target_brand"
  ) {
    return {
      entityRole: "customer" as const,
      scoreTreatment: getScoreTreatmentForEntityRole("customer")
    };
  }

  if (["direct_competitor", "adjacent_competitor", "suppress"].includes(normalizedRole)) {
    return {
      entityRole: normalizedRole as "direct_competitor" | "adjacent_competitor" | "suppress",
      scoreTreatment: getScoreTreatmentForEntityRole(normalizedRole)
    };
  }

  return null;
}

function isCustomerBrandMention(mention: string, customerBrand?: string | null): boolean {
  if (!customerBrand) return false;

  const normalizedMention = normalizeEntityName(mention);
  const normalizedBrand = normalizeEntityName(customerBrand);

  return normalizedMention === normalizedBrand;
}

function normalizeCategoryLabel(category?: string | null): string {
  if (!category) return "Uncategorized";

  return category
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function getCategoryKey(category?: string | null): string {
  return (category || "uncategorized")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function getScoreStatus(percent: number): ScoreStatus {
  if (percent >= 70) return "strong";
  if (percent >= 40) return "developing";
  return "weak";
}

function getStatusLabel(percent: number): string {
  const status = getScoreStatus(percent);
  if (status === "strong") return "Strong";
  if (status === "developing") return "Developing";
  return "Weak";
}

function summarizeCompetitiveRun(
  rows: PromptResultRow[],
  governanceRows: TrustedEntityGovernanceRow[],
  customerBrand?: string | null
) {
  const totalPrompts = rows.length;
  const marketMentionRows = rows.filter(isOrganicMarketMentionPrompt);
  const marketMentionPromptCount = marketMentionRows.length;

  const avgScore =
    totalPrompts > 0
      ? rows.reduce((sum, row) => sum + toNumber(row.prompt_score), 0) / totalPrompts
      : 0;

  const visibilityPct = scoreToPercent(avgScore);

  const strongSignals = rows.filter((row) => row.visibility_signal === "strong").length;
  const strongSignalRate =
    totalPrompts > 0 ? (strongSignals / totalPrompts) * 100 : 0;

  const customerMentionedPrompts = rows.filter((row) =>
    parseBrandMentions(row.brand_mentions).some((mention) =>
      isCustomerBrandMention(mention, customerBrand)
    )
  ).length;

  const customerMentionRate =
    totalPrompts > 0 ? (customerMentionedPrompts / totalPrompts) * 100 : 0;

  const byCategory = new Map<string, PromptResultRow[]>();

  rows.forEach((row) => {
    const key = getCategoryKey(row.category);
    const existing = byCategory.get(key) || [];
    existing.push(row);
    byCategory.set(key, existing);
  });

  const categories: CategorySummary[] = Array.from(byCategory.entries())
    .map(([category, categoryRows]) => {
      const prompts = categoryRows.length;
      const promptsWithCustomerMention = categoryRows.filter((row) =>
        parseBrandMentions(row.brand_mentions).some((mention) =>
          isCustomerBrandMention(mention, customerBrand)
        )
      ).length;

      const strongCategorySignals = categoryRows.filter(
        (row) => row.visibility_signal === "strong"
      ).length;

      const avgCategoryScore =
        prompts > 0
          ? categoryRows.reduce((sum, row) => sum + toNumber(row.prompt_score), 0) /
            prompts
          : 0;

      return {
        category,
        prompts,
        avgScore: avgCategoryScore,
        visibilityPct: scoreToPercent(avgCategoryScore),
        mentionRate:
          prompts > 0 ? (promptsWithCustomerMention / prompts) * 100 : 0,
        strongSignalRate:
          prompts > 0 ? (strongCategorySignals / prompts) * 100 : 0,
      };
    })
    .sort((a, b) => b.visibilityPct - a.visibilityPct);

  const mentionRollup = aggregateBrandMentionsByGovernance(
    marketMentionRows,
    (row) => row.brand_mentions,
    governanceRows,
    customerBrand
  );

  const mentions: MentionSummary[] = mentionRollup.displayEntities
    .map((item) => ({
      entity: item.displayName,
      canonicalKey: item.canonicalKey,
      mentions: item.count,
      rawNames: item.rawNames
    }))
    .sort((a, b) => b.mentions - a.mentions);

  return {
    totalPrompts,
    marketMentionPromptCount,
    avgScore,
    visibilityPct,
    strongSignalRate,
    customerMentionRate,
    customerMentionedPrompts,
    categories,
    mentions,
  };
}

export default function CompetitiveHistoricalVisibility({
  account,
  customerBrand,
  canReviewEntityGovernance = false,
}: CompetitiveHistoricalVisibilityProps) {
  const [runs, setRuns] = useState<RunHistoryItem[]>([]);
  const [currentRun, setCurrentRun] = useState<RunHistoryItem | null>(null);
  const [comparisonRun, setComparisonRun] = useState<RunHistoryItem | null>(null);
  const [currentRows, setCurrentRows] = useState<PromptResultRow[]>([]);
  const [comparisonRows, setComparisonRows] = useState<PromptResultRow[]>([]);
  const [entityGovernanceRows, setEntityGovernanceRows] = useState<TrustedEntityGovernanceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeReviewKey, setActiveReviewKey] = useState<string | null>(null);
  const [selectedMapTargetKey, setSelectedMapTargetKey] = useState("");
  const [actionStatus, setActionStatus] = useState("");
  const [isSavingAction, setIsSavingAction] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    setActiveReviewKey(null);
    setSelectedMapTargetKey("");
    setActionStatus("");
    setIsSavingAction(false);
  }, [account]);

  useEffect(() => {
    setSelectedMapTargetKey("");
    setActionStatus("");
  }, [activeReviewKey]);

  useEffect(() => {
    async function loadRuns() {
      if (!account) return;

      setLoading(true);

      const { data, error } = await supabase
        .from("runs")
        .select(
          "run_id, run_date, completed_at, started_at, run_label, environment, status, prompt_count"
        )
        .eq("account_id", account)
        .eq("status", "completed")
        .order("completed_at", { ascending: false, nullsFirst: false })
        .limit(12);

      if (error) {
        console.error("[CompetitiveHistoricalVisibility] Runs query error", error);
        setRuns([]);
        setCurrentRun(null);
        setComparisonRun(null);
        setLoading(false);
        return;
      }

      const runItems = (data || []) as RunHistoryItem[];
      const latest = getLatestRun(runItems);
      const previous = getPreviousRun(runItems, latest?.run_id);

      setRuns(runItems);
      setCurrentRun(latest);
      setComparisonRun(previous);
      setLoading(false);
    }

    loadRuns();
  }, [account]);

  useEffect(() => {
    async function loadGovernance() {
      if (!account) {
        setEntityGovernanceRows([]);
        return;
      }

      try {
        const rows = await loadEntityGovernanceReviewTargets(account);
        setEntityGovernanceRows(rows);
      } catch (error) {
        console.warn("[CompetitiveHistoricalVisibility] Governance query error", error);
        setEntityGovernanceRows([]);
      }
    }

    loadGovernance();
  }, [account]);

  const governanceLookup = useMemo(() => {
    const lookup = new Map<string, TrustedEntityGovernanceRow>();

    entityGovernanceRows.forEach((row) => {
      const rawKey = normalizeEntityName(String(row.raw_entity_name || ""));
      const normalizedKey = normalizeEntityName(String(row.normalized_entity_name || ""));

      if (rawKey) lookup.set(rawKey, row);
      if (normalizedKey) lookup.set(normalizedKey, row);
    });

    return lookup;
  }, [entityGovernanceRows]);

  useEffect(() => {
    async function loadCurrentResults() {
      if (!account || !currentRun?.run_id) {
        setCurrentRows([]);
        return;
      }

      const { data, error } = await supabase
        .from("prompt_results")
        .select("run_id, category, prompt_score, visibility_signal, brand_mentions, prompt_type")
        .eq("account_id", account)
        .eq("run_id", currentRun.run_id)
        .limit(1000);

      if (error) {
        console.error("[CompetitiveHistoricalVisibility] Current results query error", error);
        setCurrentRows([]);
        return;
      }

      setCurrentRows((data || []) as PromptResultRow[]);
    }

    loadCurrentResults();
  }, [account, currentRun?.run_id]);

  useEffect(() => {
    async function loadComparisonResults() {
      if (!account || !comparisonRun?.run_id) {
        setComparisonRows([]);
        return;
      }

      const { data, error } = await supabase
        .from("prompt_results")
        .select("run_id, category, prompt_score, visibility_signal, brand_mentions, prompt_type")
        .eq("account_id", account)
        .eq("run_id", comparisonRun.run_id)
        .limit(1000);

      if (error) {
        console.error("[CompetitiveHistoricalVisibility] Comparison results query error", error);
        setComparisonRows([]);
        return;
      }

      setComparisonRows((data || []) as PromptResultRow[]);
    }

    loadComparisonResults();
  }, [account, comparisonRun?.run_id]);

  const currentSummary = useMemo(
    () => summarizeCompetitiveRun(currentRows, entityGovernanceRows, customerBrand),
    [currentRows, entityGovernanceRows, customerBrand]
  );

  const comparisonSummary = useMemo(
    () => summarizeCompetitiveRun(comparisonRows, entityGovernanceRows, customerBrand),
    [comparisonRows, entityGovernanceRows, customerBrand]
  );

  const compatibility = useMemo(
    () =>
      getHistoryCompatibility({
        currentCategories: currentSummary.categories.map((category) => category.category),
        comparisonCategories: comparisonSummary.categories.map((category) => category.category),
        currentPromptCount: currentSummary.totalPrompts,
        comparisonPromptCount: comparisonRun ? comparisonSummary.totalPrompts : null,
      }),
    [
      currentSummary.categories,
      comparisonSummary.categories,
      currentSummary.totalPrompts,
      comparisonSummary.totalPrompts,
      comparisonRun,
    ]
  );

  const visibilityDelta = calculateDelta(
    currentSummary.visibilityPct,
    comparisonRun ? comparisonSummary.visibilityPct : null,
    { suffix: "%", decimals: 0 }
  );

  const mentionDelta = calculateDelta(
    currentSummary.customerMentionRate,
    comparisonRun ? comparisonSummary.customerMentionRate : null,
    { suffix: "%", decimals: 1 }
  );

  const strongDelta = calculateDelta(
    currentSummary.strongSignalRate,
    comparisonRun ? comparisonSummary.strongSignalRate : null,
    { suffix: "%", decimals: 1 }
  );

  const featuredCategory = useMemo(() => {
    if (comparisonRun) {
      const movers = currentSummary.categories
        .map((category) => {
          const previousCategory = comparisonSummary.categories.find(
            (item) => getCategoryKey(item.category) === getCategoryKey(category.category)
          );

          if (!previousCategory) return null;

          return {
            label: normalizeCategoryLabel(category.category),
            current: category.visibilityPct,
            previous: previousCategory.visibilityPct,
            change: category.visibilityPct - previousCategory.visibilityPct,
          };
        })
        .filter(Boolean) as Array<{
        label: string;
        current: number;
        previous: number;
        change: number;
      }>;

      const biggestMover = movers.sort((a, b) => b.change - a.change)[0];
      if (biggestMover) {
        return {
          label: "Biggest Gainer",
          value: biggestMover.label,
          detail: `${biggestMover.change >= 0 ? "+" : ""}${biggestMover.change.toFixed(0)} pts to ${formatPercent(biggestMover.current)}`,
          percent: biggestMover.current,
        };
      }
    }

    const strongest = currentSummary.categories[0];

    return {
      label: "Strongest Category",
      value: strongest ? normalizeCategoryLabel(strongest.category) : "Not available",
      detail: strongest ? `${formatPercent(strongest.visibilityPct)} visibility` : "Baseline",
      percent: strongest?.visibilityPct || 0,
    };
  }, [comparisonRun, currentSummary.categories, comparisonSummary.categories]);

  const topMentionRows = useMemo(() => {
    const entities = new Map<
      string,
      {
        entity: string;
        current: number;
        previous: number;
        currentPromptCount: number;
        previousPromptCount: number;
      }
    >();

    currentSummary.mentions.slice(0, 15).forEach((item) => {
      entities.set(item.canonicalKey, {
        entity: item.entity,
        current: item.mentions,
        previous: 0,
        currentPromptCount: currentSummary.marketMentionPromptCount,
        previousPromptCount: comparisonSummary.marketMentionPromptCount,
      });
    });

    comparisonSummary.mentions.forEach((item) => {
      const key = item.canonicalKey;
      const existing = entities.get(key);

      if (existing) {
        existing.previous = item.mentions;
        existing.previousPromptCount = comparisonSummary.marketMentionPromptCount;
      }
    });

    return Array.from(entities.values()).sort((a, b) => b.current - a.current);
  }, [
    currentSummary.mentions,
    currentSummary.marketMentionPromptCount,
    comparisonSummary.mentions,
    comparisonSummary.marketMentionPromptCount
  ]);

  const activeReviewEntity = useMemo(() => {
    if (!activeReviewKey) return null;
    return topMentionRows.find((row) => row.entity === activeReviewKey) || null;
  }, [activeReviewKey, topMentionRows]);

  const activeReviewGovernance = useMemo(() => {
    if (!activeReviewEntity) return null;
    return governanceLookup.get(normalizeEntityName(activeReviewEntity.entity)) || null;
  }, [activeReviewEntity, governanceLookup]);

  const reviewTargetOptions = useMemo(() => {
    const options: ReviewTargetOption[] = [];
    const seenKeys = new Set<string>();

    const addOption = (input: {
      displayName: string;
      role: string | null | undefined;
      scoreTreatment: string | null;
      source: string | null;
      canonicalName?: string | null;
      rawName?: string | null;
      isCustomer?: boolean;
    }) => {
      if (!isSelectableReviewTargetRole(input.role)) {
        return;
      }

      const key = normalizeEntityName(
        input.canonicalName || input.displayName || input.rawName || ""
      );

      if (!key || seenKeys.has(key)) {
        return;
      }

      seenKeys.add(key);
      options.push({
        key,
        displayName: input.displayName.trim(),
        role: normalizeGovernanceRole(input.role),
        scoreTreatment: getScoreTreatmentForEntityRole(input.role),
        source: input.source,
        isCustomer: Boolean(input.isCustomer)
      });
    };

    topMentionRows.forEach((row) => {
      const matched = governanceLookup.get(normalizeEntityName(row.entity));
      if (matched) {
        const normalizedTarget = normalizeEntityReviewTarget(matched, customerBrand);

        if (!normalizedTarget) return;

        addOption({
          displayName: normalizedTarget.displayName,
          role: normalizedTarget.role,
          scoreTreatment: normalizedTarget.scoreTreatment,
          source: normalizedTarget.source,
          canonicalName: normalizedTarget.displayName,
          rawName: matched.raw_entity_name,
          isCustomer: normalizedTarget.isCustomer
        });
      } else if (
        customerBrand &&
        normalizeEntityName(row.entity) === normalizeEntityName(customerBrand)
      ) {
        addOption({
          displayName: customerBrand,
          role: "customer",
          scoreTreatment: "include_primary",
          source: "account_context",
          canonicalName: customerBrand,
          rawName: customerBrand,
          isCustomer: true
        });
      }
    });

    entityGovernanceRows.forEach((row) => {
      const normalizedTarget = normalizeEntityReviewTarget(row, customerBrand);

      if (!normalizedTarget) return;

      addOption({
        displayName: normalizedTarget.displayName,
        role: normalizedTarget.role,
        scoreTreatment: normalizedTarget.scoreTreatment,
        source: normalizedTarget.source,
        canonicalName: normalizedTarget.displayName,
        rawName: row.raw_entity_name,
        isCustomer: normalizedTarget.isCustomer
      });
    });

    return options.slice(0, 25);
  }, [topMentionRows, governanceLookup, entityGovernanceRows, customerBrand]);

  async function refreshGovernance() {
    if (!account) return;

    try {
      const rows = await loadEntityGovernanceReviewTargets(account);
      if (isMountedRef.current) {
        setEntityGovernanceRows(rows);
      }
    } catch (error) {
      console.warn("[CompetitiveHistoricalVisibility] Governance refresh failed", error);
    }
  }

  async function commitEntityGovernanceAction(
    entityName: string,
    action:
      | "direct_competitor"
      | "adjacent_competitor"
      | "suppress"
      | "needs_review"
      | "map_existing",
    mapTarget?: ReviewTargetOption | null
  ) {
    if (!canReviewEntityGovernance || !account || !entityName.trim()) {
      return;
    }

    const existing = governanceLookup.get(normalizeEntityName(entityName));
    const mappedName = entityName.trim();
    const selectedTarget = mapTarget || null;

    const entityRole =
      action === "direct_competitor"
        ? "direct_competitor"
        : action === "adjacent_competitor"
          ? "adjacent_competitor"
          : action === "suppress"
            ? "suppress"
            : "unknown";

    const mappedConfig =
      action === "map_existing" && selectedTarget
        ? getMapExistingWriteConfig(selectedTarget.role)
        : null;

    if (action === "map_existing") {
      if (!selectedTarget) {
        if (isMountedRef.current) {
          setActionStatus("Choose an existing entity before mapping.");
        }
        return;
      }

      if (!mappedConfig) {
        if (isMountedRef.current) {
          setActionStatus("Selected target cannot be used for mapping.");
        }
        return;
      }
    }

    const rationale =
      action === "direct_competitor"
        ? "Marked directly from AI market mention review."
        : action === "adjacent_competitor"
          ? "Marked as adjacent competitor from AI market mention review."
          : action === "suppress"
            ? "Suppressed from AI market mention review."
            : `Mapped from surfaced mention ${entityName} to existing entity ${selectedTarget?.displayName || entityName}.`;

    const targetRole = mappedConfig?.entityRole || (action === "map_existing" ? "unknown" : entityRole);
    const targetScoreTreatment = mappedConfig
      ? getScoreTreatmentForEntityRole(mappedConfig.entityRole)
      : getScoreTreatmentForEntityRole(targetRole);
    const targetName = selectedTarget?.displayName || mappedName;

    setIsSavingAction(true);
    setActionStatus("");
    setActiveReviewKey(entityName);

    try {
      if (existing) {
        await updateEntityRole(existing.id, {
          raw_entity_name: entityName.trim(),
          normalized_entity_name: action === "map_existing" ? targetName : mappedName,
          parent_entity_name: action === "map_existing" ? targetName : null,
          entity_role: targetRole,
          score_treatment: targetScoreTreatment,
          rationale,
          is_active: true
        });
      } else {
        await createEntityRole({
          account_id: account,
          market_profile_id: null,
          raw_entity_name: entityName.trim(),
          normalized_entity_name: action === "map_existing" ? targetName : mappedName,
          parent_entity_name: action === "map_existing" ? targetName : null,
          entity_role: targetRole,
          score_treatment: targetScoreTreatment,
          confidence_score: null,
          rationale,
          admin_notes: null
        });
      }

      await refreshGovernance();
      if (isMountedRef.current) {
        setActionStatus(`${entityName} updated.`);
      }
    } catch (error: any) {
      console.error("[CompetitiveHistoricalVisibility] Entity governance action failed", error);
      if (isMountedRef.current) {
        setActionStatus(error?.message || "Failed to update entity governance.");
      }
    } finally {
      if (isMountedRef.current) {
        setIsSavingAction(false);
      }
    }
  }

  function handleComparisonRunChange(runId: string) {
    const selected = runs.find((run) => run.run_id === runId) || null;
    setComparisonRun(selected);
  }

  if (loading) {
    return (
      <section style={styles.panel}>
        <p style={styles.eyebrow}>Historical performance</p>
        <h2 style={styles.title}>Loading competitive visibility history…</h2>
      </section>
    );
  }

  if (!currentRun) {
    return (
      <section style={styles.panel}>
        <p style={styles.eyebrow}>Historical performance</p>
        <h2 style={styles.title}>Competitive visibility history</h2>
        <p style={styles.muted}>
          No completed runs are available yet. Once this account has a completed run,
          Toren will begin building a competitive benchmark.
        </p>
      </section>
    );
  }

  return (
    <section style={styles.panel}>
      <div style={styles.header}>
        <div>
          <p style={styles.eyebrow}>Historical performance</p>
          <h2 style={styles.title}>Competitive visibility history</h2>
          <p style={styles.subtitle}>
            Track how this brand is moving against competitors across AI recommendation prompts.
          </p>
        </div>
      </div>

      <RunComparisonBar
        currentRun={currentRun}
        comparisonRun={comparisonRun}
        availableRuns={runs}
        onComparisonRunChange={handleComparisonRunChange}
      />

      {compatibility.status === "directional" && (
        <div style={styles.promptCountNotice}>
          {compatibility.message} {currentSummary.totalPrompts} current prompts vs{" "}
          {comparisonSummary.totalPrompts} previous prompts.
        </div>
      )}

      {compatibility.status === "comparable" && (
        <div style={styles.comparableNotice}>{compatibility.message}</div>
      )}

      {compatibility.status === "baseline" && (
        <div style={styles.baselineNotice}>{compatibility.message}</div>
      )}

      <div style={styles.metricGrid}>
        <MetricCard
          label="Visibility Rate"
          value={formatPercent(currentSummary.visibilityPct)}
          delta={visibilityDelta.label}
          direction={visibilityDelta.direction}
          tooltip="A normalized percentage from the underlying visibility score. 0–39% is weak, 40–69% is developing, and 70%+ is strong."
          statusPercent={currentSummary.visibilityPct}
        />
        <MetricCard
          label="Brand Mention Rate"
          value={`${currentSummary.customerMentionRate.toFixed(1)}%`}
          delta={mentionDelta.label}
          direction={mentionDelta.direction}
          tooltip="The share of tracked prompts where this brand was mentioned at least once. This is often the easiest visibility metric for clients to understand."
          statusPercent={currentSummary.customerMentionRate}
        />
        <MetricCard
          label="Strong Signal Rate"
          value={`${currentSummary.strongSignalRate.toFixed(1)}%`}
          delta={strongDelta.label}
          direction={strongDelta.direction}
          tooltip="The share of prompts where the brand received a strong visibility signal, not just a passing mention."
          statusPercent={currentSummary.strongSignalRate}
        />
        <MetricCard
          label={featuredCategory.label}
          value={featuredCategory.value}
          delta={featuredCategory.detail}
          direction="new"
          tooltip="This replaces tracked prompt count with a more useful narrative metric: either the strongest current buyer-journey category or the biggest category gainer when a previous run exists."
          statusPercent={featuredCategory.percent}
          compactValue
        />
      </div>

      <div style={styles.twoColumnGrid}>
        <div style={styles.tableWrapper}>
          <div style={styles.tableHeader}>
            <h3 style={styles.tableTitle}>Visibility by buyer journey group</h3>
            <p style={styles.tableSubtitle}>
              Category scores are normalized to percentages so weak, developing, and strong areas are easier to understand.
            </p>
          </div>

          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Prompt group</th>
                <th style={styles.thRight}>Current</th>
                <th style={styles.thRight}>Previous</th>
                <th style={styles.thRight}>Change</th>
              </tr>
            </thead>
            <tbody>
              {currentSummary.categories.map((category) => {
                const previousCategory = comparisonSummary.categories.find(
                  (item) => getCategoryKey(item.category) === getCategoryKey(category.category)
                );

                const hasComparableCategory = Boolean(comparisonRun && previousCategory);

                const delta = calculateDelta(
                  category.visibilityPct,
                  hasComparableCategory ? previousCategory?.visibilityPct : null,
                  { suffix: "%", decimals: 0 }
                );

                return (
                  <tr key={category.category}>
                    <td style={styles.td}>{normalizeCategoryLabel(category.category)}</td>
                    <td style={styles.tdRight}>
                      <ScorePill percent={category.visibilityPct} />
                    </td>
                    <td style={styles.tdRight}>
                      {previousCategory ? (
                        <ScorePill percent={previousCategory.visibilityPct} compact />
                      ) : (
                        "—"
                      )}
                    </td>
                    <td style={styles.tdRight}>
                      <span style={getDeltaStyle(delta.direction)}>
                        {comparisonRun && !previousCategory ? "Not comparable" : delta.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div style={styles.tableWrapper}>
          <div style={styles.tableHeader}>
            <h3 style={styles.tableTitle}>AI market mentions</h3>
            <p style={styles.tableSubtitle}>
              Organic mention coverage across the latest run. Guided prompts are excluded; aliases are rolled up into approved entities and counted once per prompt response.
            </p>
          </div>

          {canReviewEntityGovernance && activeReviewEntity ? (
            <div style={styles.reviewPanel}>
              <div style={styles.reviewPanelHeader}>
                <div>
                  <p style={styles.reviewPanelEyebrow}>Review market entity</p>
                  <h4 style={styles.reviewPanelTitle}>{activeReviewEntity.entity}</h4>
                  <p style={styles.reviewPanelSubtitle}>
                    Review the surfaced entity and update trusted governance without changing table layout.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setActionStatus("");
                    setActiveReviewKey(null);
                  }}
                  style={styles.reviewCloseButton}
                >
                  Close
                </button>
              </div>

              <div style={styles.reviewMetaGrid}>
                <div style={styles.reviewMetaItem}>
                  <span style={styles.reviewMetaLabel}>Selected entity</span>
                  <strong style={styles.reviewMetaValue}>{activeReviewEntity.entity}</strong>
                </div>
                <div style={styles.reviewMetaItem}>
                  <span style={styles.reviewMetaLabel}>Mapped / canonical</span>
                  <strong style={styles.reviewMetaValue}>
                    {activeReviewGovernance?.normalized_entity_name ||
                      activeReviewGovernance?.parent_entity_name ||
                      "Not mapped yet"}
                  </strong>
                </div>
                <div style={styles.reviewMetaItem}>
                  <span style={styles.reviewMetaLabel}>Current role / status</span>
                  <strong style={styles.reviewMetaValue}>
                    {activeReviewGovernance
                      ? [
                          activeReviewGovernance.entity_role || "unknown role",
                          activeReviewGovernance.is_active ? "active" : "inactive",
                          activeReviewGovernance.approved_at ? "approved" : "pending"
                        ].join(" · ")
                      : "No trusted governance row yet"}
                  </strong>
                </div>
              </div>

              <div style={styles.reviewActionsGrid}>
                {[
                  ["direct_competitor", "Mark as competitor"],
                  ["adjacent_competitor", "Mark as adjacent competitor"],
                  ["suppress", "Suppress / not relevant"],
                  ["needs_review", "Not sure"]
                ].map(([action, label]) => (
                  <button
                    key={action}
                    type="button"
                    disabled={isSavingAction}
                    onClick={() =>
                      commitEntityGovernanceAction(
                        activeReviewEntity.entity,
                        action as
                          | "direct_competitor"
                          | "adjacent_competitor"
                          | "suppress"
                          | "needs_review"
                      )
                    }
                    style={{
                      border: "1px solid #cbd5e1",
                      background: "white",
                      color: "#0f172a",
                      borderRadius: 10,
                      padding: "8px 12px",
                      fontSize: 13,
                      fontWeight: 800,
                      cursor: isSavingAction ? "not-allowed" : "pointer",
                      textAlign: "left"
                    }}
                  >
                    {isSavingAction && activeReviewEntity.entity === activeReviewKey ? "Saving..." : label}
                  </button>
                ))}
              </div>

              <div style={styles.mapExistingPanel}>
                <div style={styles.mapExistingHeader}>
                  <div>
                    <p style={styles.reviewMetaLabel}>Map to existing entity</p>
                    <p style={styles.mapExistingHelp}>
                      Choose one of the account’s existing governed entities. The selected target’s role determines a safe scoring treatment before writing.
                    </p>
                    <p style={styles.mapExistingHelp}>
                      Primary = customer visibility · Secondary = competitor / market visibility · Suppressed = excluded from market view
                    </p>
                  </div>
                </div>
                <label style={styles.mapExistingLabel}>
                  Target entity
                  <select
                    aria-label="Map to existing entity target"
                    value={selectedMapTargetKey}
                    onChange={(event) => setSelectedMapTargetKey(event.target.value)}
                    style={styles.mapExistingSelect}
                  >
                    <option value="">Choose an existing entity…</option>
                    {reviewTargetOptions.map((option) => (
                      <option key={option.key} value={option.key}>
                        {option.displayName} · {getRoleLabel(option.role)}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  disabled={isSavingAction || !selectedMapTargetKey}
                  onClick={() => {
                    const selectedTarget =
                      reviewTargetOptions.find((option) => option.key === selectedMapTargetKey) || null;

                    if (!selectedTarget) {
                      setActionStatus("Choose an existing entity before mapping.");
                      return;
                    }

                    commitEntityGovernanceAction(
                      activeReviewEntity.entity,
                      "map_existing",
                      selectedTarget
                    );
                  }}
                  style={{
                    border: "1px solid #cbd5e1",
                    background: selectedMapTargetKey ? "#0f172a" : "#e2e8f0",
                    color: selectedMapTargetKey ? "#fff" : "#64748b",
                    borderRadius: 10,
                    padding: "8px 12px",
                    fontSize: 13,
                    fontWeight: 800,
                    cursor: isSavingAction || !selectedMapTargetKey ? "not-allowed" : "pointer",
                    textAlign: "left",
                    width: "100%"
                  }}
                >
                  Map selected entity
                </button>
              </div>

              {actionStatus ? <div style={styles.reviewStatus}>{actionStatus}</div> : null}
            </div>
          ) : null}

          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Entity</th>
                <th
                  style={styles.thRight}
                  title="Organic prompts only. Guided prompts are excluded; aliases are rolled up into approved entities and counted once per prompt response."
                >
                  Mention coverage
                </th>
                <th style={styles.thRight}>Previous coverage</th>
                <th style={styles.thRight}>Change</th>
              </tr>
            </thead>
            <tbody>
              {topMentionRows.map((row) => {
                const currentCoverage = calculateMentionCoverage(
                  row.current,
                  row.currentPromptCount
                );
                const previousCoverage = calculateMentionCoverage(
                  row.previous,
                  row.previousPromptCount
                );
                const delta = calculateDelta(
                  currentCoverage,
                  comparisonRun ? previousCoverage : null,
                  { suffix: "%", decimals: 0 }
                );

                const isCustomer =
                  customerBrand &&
                  normalizeEntityName(row.entity) === normalizeEntityName(customerBrand);

                return (
                  <tr key={row.entity}>
                    <td style={styles.td}>
                      <span style={styles.entityLabelRow}>
                        <span>{row.entity}</span>
                        {canReviewEntityGovernance ? (
                          <button
                            type="button"
                            aria-label={`Review ${row.entity}`}
                            onClick={() => {
                              setActionStatus("");
                              setActiveReviewKey(activeReviewKey === row.entity ? null : row.entity);
                            }}
                            style={styles.reviewTrigger}
                          >
                            Review
                          </button>
                        ) : null}
                      </span>
                      {isCustomer && <span style={styles.customerBadge}>Your brand</span>}
                    </td>
                    <td style={styles.tdRight}>
                      <MentionCoverageCell count={row.current} promptCount={row.currentPromptCount} />
                    </td>
                    <td style={styles.tdRight}>
                      {comparisonRun ? (
                        <MentionCoverageCell count={row.previous} promptCount={row.previousPromptCount} compact />
                      ) : (
                        "—"
                      )}
                    </td>
                    <td style={styles.tdRight}>
                      <span style={getDeltaStyle(delta.direction)}>{delta.label}</span>
                    </td>
                  </tr>
                );
              })}

              {topMentionRows.length === 0 && (
                <tr>
                  <td style={styles.emptyCell} colSpan={4}>
                    No organic market mentions were detected for this run.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function MentionCoverageCell({
  count,
  promptCount,
  compact = false
}: {
  count: number;
  promptCount: number;
  compact?: boolean;
}) {
  const coverage = calculateMentionCoverage(count, promptCount);

  return (
    <span title="Aliases are rolled up into approved entities and counted once per prompt response.">
      <strong>{coverage}%</strong>
      <span style={{ display: "block", color: "#64748b", fontSize: compact ? 11 : 12, marginTop: 2 }}>
        {count} / {promptCount} prompts
      </span>
    </span>
  );
}

function MetricCard({
  label,
  value,
  delta,
  direction,
  tooltip,
  statusPercent,
  compactValue,
}: {
  label: string;
  value: string;
  delta: string;
  direction: "up" | "down" | "flat" | "new";
  tooltip: string;
  statusPercent?: number;
  compactValue?: boolean;
}) {
  const statusStyles = getStatusCardStyle(statusPercent);

  return (
    <div style={{ ...styles.metricCard, ...statusStyles }}>
      <p style={styles.metricLabel}>
        {label}
        <Tooltip text={tooltip} />
      </p>
      <div style={compactValue ? styles.metricValueCompact : styles.metricValue}>{value}</div>
      <p style={getDeltaStyle(direction)}>{delta}</p>
      {typeof statusPercent === "number" && (
        <span style={getStatusBadgeStyle(statusPercent)}>{getStatusLabel(statusPercent)}</span>
      )}
    </div>
  );
}

function ScorePill({ percent, compact = false }: { percent: number; compact?: boolean }) {
  return (
    <span style={getStatusBadgeStyle(percent, compact)}>
      {formatPercent(percent)} {!compact && getStatusLabel(percent)}
    </span>
  );
}

function getDeltaStyle(direction: "up" | "down" | "flat" | "new"): React.CSSProperties {
  if (direction === "up") return styles.deltaUp;
  if (direction === "down") return styles.deltaDown;
  if (direction === "flat") return styles.deltaFlat;
  return styles.deltaNew;
}

function getStatusCardStyle(percent?: number): React.CSSProperties {
  if (typeof percent !== "number") return {};
  const status = getScoreStatus(percent);
  if (status === "strong") return styles.cardStrong;
  if (status === "developing") return styles.cardDeveloping;
  return styles.cardWeak;
}

function getStatusBadgeStyle(percent: number, compact = false): React.CSSProperties {
  const status = getScoreStatus(percent);
  const base = compact ? styles.statusBadgeCompact : styles.statusBadge;

  if (status === "strong") return { ...base, ...styles.statusStrong };
  if (status === "developing") return { ...base, ...styles.statusDeveloping };
  return { ...base, ...styles.statusWeak };
}

const styles: Record<string, React.CSSProperties> = {
  panel: {
    border: "1px solid #e5e7eb",
    borderRadius: 20,
    padding: 20,
    background: "#f8fafc",
    marginBottom: 24,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    gap: 16,
    marginBottom: 16,
  },
  eyebrow: {
    margin: 0,
    color: "#64748b",
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  },
  title: {
    margin: "4px 0 0",
    fontSize: 22,
    color: "#0f172a",
  },
  subtitle: {
    margin: "6px 0 0",
    color: "#475569",
    fontSize: 14,
  },
  muted: {
    color: "#64748b",
    fontSize: 14,
  },
  baselineNotice: {
    border: "1px solid #bfdbfe",
    background: "#eff6ff",
    color: "#1e3a8a",
    borderRadius: 14,
    padding: 14,
    fontSize: 14,
    marginBottom: 16,
  },
  promptCountNotice: {
    border: "1px solid #fde68a",
    background: "#fffbeb",
    color: "#92400e",
    borderRadius: 14,
    padding: 12,
    fontSize: 13,
    fontWeight: 700,
    marginBottom: 16,
  },
  comparableNotice: {
    border: "1px solid #bbf7d0",
    background: "#f0fdf4",
    color: "#166534",
    borderRadius: 14,
    padding: 12,
    fontSize: 13,
    fontWeight: 700,
    marginBottom: 16,
  },
  metricGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 12,
    marginBottom: 18,
  },
  metricCard: {
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: 16,
    padding: 16,
  },
  cardStrong: {
    borderLeft: "5px solid #16a34a",
  },
  cardDeveloping: {
    borderLeft: "5px solid #f59e0b",
  },
  cardWeak: {
    borderLeft: "5px solid #dc2626",
  },
  metricLabel: {
    margin: 0,
    color: "#64748b",
    fontSize: 13,
    fontWeight: 800,
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
  },
  metricValue: {
    marginTop: 8,
    fontSize: 28,
    fontWeight: 900,
    color: "#0f172a",
  },
  metricValueCompact: {
    marginTop: 8,
    fontSize: 18,
    lineHeight: 1.15,
    fontWeight: 900,
    color: "#0f172a",
  },
  statusBadge: {
    display: "inline-flex",
    marginTop: 8,
    borderRadius: 999,
    padding: "4px 8px",
    fontSize: 11,
    fontWeight: 900,
    lineHeight: 1,
  },
  statusBadgeCompact: {
    display: "inline-flex",
    borderRadius: 999,
    padding: "4px 8px",
    fontSize: 11,
    fontWeight: 900,
    lineHeight: 1,
    whiteSpace: "nowrap",
  },
  statusStrong: {
    background: "#dcfce7",
    color: "#166534",
    border: "1px solid #bbf7d0",
  },
  statusDeveloping: {
    background: "#fef3c7",
    color: "#92400e",
    border: "1px solid #fde68a",
  },
  statusWeak: {
    background: "#fee2e2",
    color: "#991b1b",
    border: "1px solid #fecaca",
  },
  deltaUp: {
    margin: "8px 0 0",
    color: "#15803d",
    fontSize: 13,
    fontWeight: 800,
  },
  deltaDown: {
    margin: "8px 0 0",
    color: "#b91c1c",
    fontSize: 13,
    fontWeight: 800,
  },
  deltaFlat: {
    margin: "8px 0 0",
    color: "#475569",
    fontSize: 13,
    fontWeight: 800,
  },
  deltaNew: {
    margin: "8px 0 0",
    color: "#2563eb",
    fontSize: 13,
    fontWeight: 800,
  },
  twoColumnGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))",
    gap: 14,
  },
  tableWrapper: {
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: 16,
    overflow: "hidden",
  },
  tableHeader: {
    padding: 16,
    borderBottom: "1px solid #e2e8f0",
  },
  tableTitle: {
    margin: 0,
    fontSize: 16,
    color: "#0f172a",
  },
  tableSubtitle: {
    margin: "4px 0 0",
    fontSize: 13,
    color: "#64748b",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 13,
  },
  th: {
    textAlign: "left",
    padding: "10px 14px",
    color: "#64748b",
    borderBottom: "1px solid #e2e8f0",
    background: "#f8fafc",
  },
  thRight: {
    textAlign: "right",
    padding: "10px 14px",
    color: "#64748b",
    borderBottom: "1px solid #e2e8f0",
    background: "#f8fafc",
  },
  td: {
    padding: "12px 14px",
    borderBottom: "1px solid #f1f5f9",
    color: "#0f172a",
    fontWeight: 700,
    verticalAlign: "middle",
  },
  tdRight: {
    padding: "12px 14px",
    borderBottom: "1px solid #f1f5f9",
    color: "#0f172a",
    textAlign: "right",
    verticalAlign: "middle",
  },
  emptyCell: {
    padding: 18,
    textAlign: "center",
    color: "#64748b",
  },
  customerBadge: {
    display: "inline-flex",
    marginLeft: 8,
    padding: "2px 6px",
    borderRadius: 999,
    background: "#ecfeff",
    color: "#155e75",
    fontSize: 10,
    fontWeight: 900,
  },
  entityLabelRow: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  reviewTrigger: {
    border: "1px solid #bfdbfe",
    background: "#eff6ff",
    color: "#1d4ed8",
    borderRadius: 999,
    padding: "2px 8px",
    fontSize: 11,
    fontWeight: 900,
    cursor: "pointer",
    lineHeight: 1.2,
  },
  reviewPanel: {
    margin: 16,
    marginBottom: 0,
    padding: 16,
    borderRadius: 16,
    border: "1px solid #c7d2fe",
    background: "#eef2ff",
  },
  reviewPanelHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "flex-start",
    marginBottom: 14,
  },
  reviewPanelEyebrow: {
    margin: 0,
    color: "#4f46e5",
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  },
  reviewPanelTitle: {
    margin: "4px 0 0",
    fontSize: 18,
    color: "#111827",
  },
  reviewPanelSubtitle: {
    margin: "6px 0 0",
    color: "#374151",
    fontSize: 13,
  },
  reviewCloseButton: {
    border: "1px solid #cbd5e1",
    background: "#fff",
    color: "#0f172a",
    borderRadius: 999,
    padding: "8px 12px",
    fontSize: 12,
    fontWeight: 900,
    cursor: "pointer",
  },
  reviewMetaGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 10,
    marginBottom: 14,
  },
  reviewMetaItem: {
    background: "rgba(255,255,255,0.78)",
    border: "1px solid #dbeafe",
    borderRadius: 12,
    padding: 12,
  },
  reviewMetaLabel: {
    display: "block",
    color: "#6b7280",
    fontSize: 11,
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    marginBottom: 4,
  },
  reviewMetaValue: {
    display: "block",
    color: "#111827",
    fontSize: 13,
    fontWeight: 800,
  },
  reviewActionsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
    gap: 8,
  },
  reviewStatus: {
    marginTop: 12,
    color: "#166534",
    fontSize: 12,
    fontWeight: 900,
  },
  mapExistingPanel: {
    marginTop: 14,
    padding: 12,
    borderRadius: 12,
    border: "1px solid #dbeafe",
    background: "#fff",
    display: "grid",
    gap: 10,
  },
  mapExistingHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
  },
  mapExistingHelp: {
    margin: "4px 0 0",
    color: "#475569",
    fontSize: 12,
    lineHeight: 1.4,
  },
  mapExistingLabel: {
    display: "grid",
    gap: 6,
    color: "#0f172a",
    fontSize: 13,
    fontWeight: 800,
  },
  mapExistingSelect: {
    width: "100%",
    border: "1px solid #cbd5e1",
    borderRadius: 10,
    padding: "8px 10px",
    fontSize: 13,
    color: "#0f172a",
    background: "#fff",
  },
};
